import { supabase } from '../supabase';
import { useQueryClient } from '@tanstack/react-query';
import { useEffect, useRef, useCallback } from 'react';
import { SpamShift } from '@shiftly/shared';
import { DateTime } from 'luxon';
import { convertDateFields } from './supabaseSpamShiftService';
import { useAuth } from '../../../contexts/AuthContext';
import { Permissions } from '@shiftly/shared/src/permissions';

export interface RealtimeSpamShiftManagerOptions {
  enabled?: boolean;
  onError?: (error: any) => void;
}

export const useRealtimeSpamShiftManager = (options: RealtimeSpamShiftManagerOptions = {}) => {
  const { enabled = true, onError } = options;
  const { user, permissions, session, loading } = useAuth();
  const queryClient = useQueryClient();
  const subscriptionRef = useRef<any>(null);

  // Only enable if auth is ready and user is authenticated
  const shouldEnable = enabled && !loading && !!user && !!session && permissions.includes(Permissions.SPAM_SHIFTS_MANAGE);

  const updateSpamShiftQueries = useCallback((payload: any) => {
    const { eventType, new: newRecord, old: oldRecord } = payload;

    const spamShiftQueries = queryClient.getQueriesData<SpamShift[]>({
      queryKey: ['supabase-spam-shifts']
    });

    if (process.env.NODE_ENV !== 'production') {
      console.log('Supabase spam shift realtime update:', { eventType, recordId: newRecord?.id || oldRecord?.id });
    }

    spamShiftQueries.forEach(([queryKey, oldData]) => {
      if (!Array.isArray(oldData)) return;

      queryClient.setQueryData(queryKey, (currentData: SpamShift[] | undefined) => {
        if (!currentData) return currentData;

        switch (eventType) {
          case 'INSERT': {
            const shift = convertDateFields(newRecord);
            if (shouldIncludeSpamShiftInQuery(shift, queryKey)) {
              const exists = currentData.some(existingShift => existingShift.id === shift.id);
              if (!exists) {
                return [...currentData, shift].sort((a, b) =>
                  a.start_date_time.toMillis() - b.start_date_time.toMillis()
                );
              }
            }
            return currentData;
          }

          case 'UPDATE': {
            const shift = convertDateFields(newRecord);
            if (shouldIncludeSpamShiftInQuery(shift, queryKey)) {
              const existingIndex = currentData.findIndex(existingShift => existingShift.id === shift.id);
              if (existingIndex >= 0) {
                const newData = [...currentData];
                newData[existingIndex] = shift;
                return newData.sort((a, b) => a.start_date_time.toMillis() - b.start_date_time.toMillis());
              } else {
                return [...currentData, shift].sort((a, b) =>
                  a.start_date_time.toMillis() - b.start_date_time.toMillis()
                );
              }
            } else {
              return currentData.filter(existingShift => existingShift.id !== shift.id);
            }
          }

          case 'DELETE': {
            return currentData.filter(shift => shift.id !== oldRecord.id);
          }

          default:
            return currentData;
        }
      });
    });
  }, [queryClient]);

  const shouldIncludeSpamShiftInQuery = (spamShift: SpamShift, queryKey: readonly unknown[]): boolean => {
    const queryParams = queryKey[1] as any;
    if (!queryParams) return true;

    if (queryParams.group && spamShift.group !== queryParams.group) {
      return false;
    }

    if (queryParams.user_id && spamShift.user_id !== queryParams.user_id) {
      return false;
    }

    if (queryParams.startDate && spamShift.start_date_time < queryParams.startDate) {
      return false;
    }

    if (queryParams.endDate && spamShift.start_date_time > queryParams.endDate) {
      return false;
    }

    if (queryParams.filters) {
      return queryParams.filters.every((filter: any) => {
        const { field, operator, value } = filter;
        const spamShiftValue = getNestedValue(spamShift, field);

        switch (operator) {
          case 'eq':
            return spamShiftValue === value;
          case 'neq':
            return spamShiftValue !== value;
          case 'gt':
            if (DateTime.isDateTime(spamShiftValue)) {
              const compareValue = DateTime.isDateTime(value) ? value : DateTime.fromISO(value);
              return spamShiftValue.toMillis() > compareValue.toMillis();
            }
            return spamShiftValue > value;
          case 'gte':
            if (DateTime.isDateTime(spamShiftValue)) {
              const compareValue = DateTime.isDateTime(value) ? value : DateTime.fromISO(value);
              return spamShiftValue.toMillis() >= compareValue.toMillis();
            }
            return spamShiftValue >= value;
          case 'lt':
            if (DateTime.isDateTime(spamShiftValue)) {
              const compareValue = DateTime.isDateTime(value) ? value : DateTime.fromISO(value);
              return spamShiftValue.toMillis() < compareValue.toMillis();
            }
            return spamShiftValue < value;
          case 'lte':
            if (DateTime.isDateTime(spamShiftValue)) {
              const compareValue = DateTime.isDateTime(value) ? value : DateTime.fromISO(value);
              return spamShiftValue.toMillis() <= compareValue.toMillis();
            }
            return spamShiftValue <= value;
          case 'in':
            return Array.isArray(value) && value.includes(spamShiftValue);
          case 'not-in':
            return Array.isArray(value) && !value.includes(spamShiftValue);
          case 'like':
            return typeof spamShiftValue === 'string' && typeof value === 'string' &&
              spamShiftValue.includes(value.replace('%', ''));
          case 'ilike':
            return typeof spamShiftValue === 'string' && typeof value === 'string' &&
              spamShiftValue.toLowerCase().includes(value.toLowerCase().replace('%', ''));
          default:
            return true;
        }
      });
    }

    return true;
  };

  const getNestedValue = (obj: any, path: string): any => {
    return path.split('.').reduce((current, key) => current?.[key], obj);
  };

  useEffect(() => {
    if (!shouldEnable) return;

    const { data: listener } = supabase.auth.onAuthStateChange((_event, session) => {
      if (session) {
        if (subscriptionRef.current) subscriptionRef.current.unsubscribe();
        subscriptionRef.current = supabase
          .channel('realtime spam shift subscription', { config: { private: true } })
          .on(
            'postgres_changes',
            {
              event: '*',
              schema: 'public',
              table: 'spam_shifts',
            },
            (payload) => {
              updateSpamShiftQueries(payload);
            }
          )
          .subscribe((status, err) => {
            console.log('Realtime spam shift subscription status:', status);
            if (err && onError) {
              onError(err);
            }
          });
      } else {
        console.log('User signed out, cleaning up realtime spam shift subscription');
        if (subscriptionRef.current) {
          subscriptionRef.current.unsubscribe();
          subscriptionRef.current = null;
        }
      }
    });

    return () => {
      console.log('Cleaning up realtime spam shift subscription');
      listener?.subscription.unsubscribe();
      if (subscriptionRef.current) {
        subscriptionRef.current.unsubscribe();
        subscriptionRef.current = null;
      }
    };
  }, [shouldEnable, updateSpamShiftQueries, onError]);

  return {
    isConnected: !!subscriptionRef.current
  };
};